import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject, Input } from '@angular/core';
import { UtilsModule } from '@vsolv/ui-kit/utils';
import { ButtonModule } from '@vsolv/vectors-ui/button';
import { IconsModule } from '@vsolv/vectors-ui/icons';
import { ThemingModule } from '@vsolv/vectors-ui/theming';
import { HighlightModule } from 'ngx-highlightjs';

@Component({
  standalone: true,
  selector: 'ws-json-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, HighlightModule, UtilsModule, ButtonModule, IconsModule, ThemingModule, ClipboardModule],
  styles: [
    `
      pre {
        opacity: 0;
        transition: opacity 0.3s 0.3s linear;
      }

      pre[data-highlighted='true'] {
        opacity: 1;
      }
    `,
  ],
  template: `
    <div class="bg-base-white rounded-xl border border-gray-200 overflow-auto scrollbar-thin">
      <div
        *ngIf="title"
        class="px-4 py-3 border-b border-gray-200 bg-gray-100 text-gray-900 flex justify-between items-center"
      >
        <h1 class="truncate text-sm font-semibold" [innerHTML]="title | safe: 'html'"></h1>

        <button
          *ngIf="json_string"
          vs-icon-button
          size="xs"
          appearance="clear"
          [cdkCopyToClipboard]="json_string"
          [theme]="copied ? 'success' : 'default'"
          (click)="copied = true"
        >
          <vs-icon size="16" [name]="copied ? 'check' : 'copy-01'"></vs-icon>
        </button>
      </div>

      <pre
        #pre
      ><code [highlight]="json_string" language="json" (highlighted)="pre.setAttribute('data-highlighted', 'true')"></code></pre>
    </div>
  `,
})
export class JsonPreviewComponent {
  private changeDetector = inject(ChangeDetectorRef);

  @HostBinding('class') protected _class = 'block';

  @Input() title?: string | null = null;
  @Input() json?: string | object | null = null;

  private timeout: ReturnType<typeof setTimeout> | null = null;

  private _copied = false;
  protected get copied() {
    return this._copied;
  }
  protected set copied(copied: boolean) {
    this._copied = copied;
    this.changeDetector.markForCheck();

    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this._copied = false;
      this.changeDetector.markForCheck();
    }, 1500);
  }

  protected get json_string() {
    if (!this.json) return 'null';

    try {
      const obj = typeof this.json === 'string' ? JSON.parse(this.json) : this.json;
      return JSON.stringify(obj, null, 2);
    } catch (error) {
      console.error('Invalid JSON input:', error);
      return 'null'; // Fallback to 'null' if parsing fails
    }
  }
}
